import { useEffect, useRef, useState } from "react";
import api, { API, formatErr } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  FileText,
  Upload,
  Download,
  FileSpreadsheet,
  Eye,
} from "lucide-react";

const empty = {
  worker_id: "",
  name: "",
  password: "1234",
  phone: "",
  address: "",
  designation: "",
  joining_date: "",
  project_id: "",
  offer_letter_base64: "",
  offer_letter_filename: "",
  dept: "",
  dob: "",
  gender: "",
  emergency_mobile: "",
  email: "",
  pan_no: "",
  aadhar_no: "",
  edu_qualification: "",
  father_name: "",
  blood_group: "",
  cl_balance: 0,
  resigned: false,
  relieving_letter_base64: "",
  relieving_letter_filename: "",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Workers() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  // Bulk upload state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkProject, setBulkProject] = useState("none");
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPwd, setBulkPwd] = useState("changeme123");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkFileRef = useRef(null);

  // View dialog
  const [viewing, setViewing] = useState(null);

  // Active/Resigned tab
  const [tab, setTab] = useState("active");

  // Search
  const [q, setQ] = useState("");

  const load = async () => {
    const [w, p] = await Promise.all([
      api.get(`/workers?resigned=${tab === "resigned"}`),
      api.get("/projects"),
    ]);
    setRows(w.data);
    setProjects(p.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tab]);

  // Upload helper for relieving letter
  const onRelievingFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    const b64 = await fileToBase64(file);
    setForm((f) => ({
      ...f,
      relieving_letter_base64: b64,
      relieving_letter_filename: file.name,
    }));
    toast.success("Relieving letter attached");
  };

  const downloadRelievingLetter = async (id) => {
    try {
      const r = await api.get(`/workers/${id}/relieving-letter`);
      const data = r.data.relieving_letter_base64;
      if (!data) {
        toast.error("No relieving letter uploaded");
        return;
      }
      const a = document.createElement("a");
      a.href = data;
      a.download = r.data.relieving_letter_filename || "relieving_letter";
      a.click();
    } catch {
      toast.error("Failed to download");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    const b64 = await fileToBase64(file);
    setForm((f) => ({
      ...f,
      offer_letter_base64: b64,
      offer_letter_filename: file.name,
    }));
    toast.success("File attached");
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.worker_id;
        if (!payload.offer_letter_base64) {
          delete payload.offer_letter_base64;
          delete payload.offer_letter_filename;
        }
        if (!payload.relieving_letter_base64) {
          delete payload.relieving_letter_base64;
          delete payload.relieving_letter_filename;
        }
        await api.put(`/workers/${editing}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/workers", form);
        toast.success("Employee added");
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const edit = (row) => {
    setForm({
      worker_id: row.worker_id,
      name: row.name,
      password: "",
      phone: row.phone || "",
      address: row.address || "",
      designation: row.designation || "",
      joining_date: row.joining_date || "",
      project_id: row.project_id || "",
      offer_letter_base64: "",
      offer_letter_filename: row.offer_letter_filename || "",
      dept: row.dept || "",
      dob: row.dob || "",
      gender: row.gender || "",
      emergency_mobile: row.emergency_mobile || "",
      email: row.email || "",
      pan_no: row.pan_no || "",
      aadhar_no: row.aadhar_no || "",
      edu_qualification: row.edu_qualification || "",
      father_name: row.father_name || "",
      blood_group: row.blood_group || "",
      cl_balance: row.cl_balance ?? 0,
      resigned: !!row.resigned,
      relieving_letter_base64: "",
      relieving_letter_filename: row.relieving_letter_filename || "",
    });
    setEditing(row.id);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete employee?")) return;
    await api.delete(`/workers/${id}`);
    toast.success("Deleted");
    load();
  };

  const downloadLetter = async (id) => {
    try {
      const r = await api.get(`/workers/${id}/offer-letter`);
      const data = r.data.offer_letter_base64;
      if (!data) {
        toast.error("No offer letter uploaded");
        return;
      }
      const a = document.createElement("a");
      a.href = data;
      a.download = r.data.offer_letter_filename || "offer_letter";
      a.click();
    } catch {
      toast.error("Failed to download");
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("itcot_token");
    const r = await fetch(`${API}/workers/bulk-template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "employees_template.xlsx";
    a.click();
  };

  const downloadAll = async () => {
    try {
      const token = localStorage.getItem("itcot_token");
      const r = await fetch(`${API}/workers/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "employees_export.xlsx";
      a.click();
      toast.success("Excel downloaded");
    } catch {
      toast.error("Failed to download");
    }
  };

  const bulkSubmit = async () => {
    if (!bulkFile) {
      toast.error("Choose an Excel file");
      return;
    }
    setBulkBusy(true);
    setBulkResult(null);
    const fd = new FormData();
    fd.append("file", bulkFile);
    if (bulkProject !== "none") fd.append("project_id", bulkProject);
    fd.append("default_password", bulkPwd || "changeme123");
    try {
      const token = localStorage.getItem("itcot_token");
      const r = await fetch(`${API}/workers/bulk`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Upload failed");
      setBulkResult(data);
      toast.success(`Imported ${data.created} employees`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="workers-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-2xl text-[#1A3A5C]">
            {tab === "active" ? "Employees" : "Resigned Employees"}
          </h2>
          <p className="text-sm text-[#475569]">
            {tab === "active"
              ? "Onboard employees and assign to projects"
              : "Archive of employees who have resigned"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border bg-white p-0.5">
            <button
              data-testid="tab-active"
              onClick={() => setTab("active")}
              className={`px-3 py-1.5 text-sm font-semibold rounded ${
                tab === "active"
                  ? "bg-[#1A3A5C] text-white"
                  : "text-[#475569]"
              }`}
            >
              Active
            </button>
            <button
              data-testid="tab-resigned"
              onClick={() => setTab("resigned")}
              className={`px-3 py-1.5 text-sm font-semibold rounded ${
                tab === "resigned"
                  ? "bg-[#1A3A5C] text-white"
                  : "text-[#475569]"
              }`}
            >
              Resigned
            </button>
          </div>
          <Button
            variant="outline"
            data-testid="download-all-btn"
            onClick={downloadAll}
            className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white"
          >
            <Download className="w-4 h-4 mr-1" /> Download All
          </Button>
          <Button
            variant="outline"
            data-testid="bulk-upload-btn"
            onClick={() => {
              setBulkOpen(true);
              setBulkResult(null);
              setBulkFile(null);
            }}
            className="border-[#2A8A8F] text-[#2A8A8F]"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Bulk Upload
          </Button>
          <Dialog
            open={open}
            onOpenChange={(o) => {
              setOpen(o);
              if (!o) {
                setEditing(null);
                setForm(empty);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                data-testid="new-worker-btn"
                className="bg-[#1A3A5C] hover:bg-[#0F2236]"
              >
                <Plus className="w-4 h-4 mr-1" /> New Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-display">
                  {editing ? "Edit Employee" : "New Employee"}
                </DialogTitle>
              </DialogHeader>
              <form
                onSubmit={save}
                className="space-y-3 max-h-[70vh] overflow-y-auto pr-2"
              >
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Emp ID *</Label>
                    <Input
                      data-testid="worker-id-field"
                      required
                      disabled={!!editing}
                      placeholder="ITP001"
                      value={form.worker_id}
                      onChange={(e) =>
                        setForm({ ...form, worker_id: e.target.value })
                      }
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Name of Employee *</Label>
                    <Input
                      data-testid="worker-name-field"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Password {editing ? "(blank = keep)" : "*"}</Label>
                    <Input
                      data-testid="worker-password-field"
                      type="password"
                      required={!editing}
                      value={form.password}
                      onChange={(e) =>
                        setForm({ ...form, password: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={form.dept}
                      onChange={(e) => setForm({ ...form, dept: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Designation</Label>
                    <Input
                      value={form.designation}
                      onChange={(e) =>
                        setForm({ ...form, designation: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Date of Joining</Label>
                    <Input
                      type="date"
                      value={form.joining_date}
                      onChange={(e) =>
                        setForm({ ...form, joining_date: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input
                      type="date"
                      value={form.dob}
                      onChange={(e) => setForm({ ...form, dob: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select
                      value={form.gender || "unset"}
                      onValueChange={(v) =>
                        setForm({ ...form, gender: v === "unset" ? "" : v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">—</SelectItem>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Blood Group</Label>
                    <Input
                      placeholder="O+ve"
                      value={form.blood_group}
                      onChange={(e) =>
                        setForm({ ...form, blood_group: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Mobile No.</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Emergency Mobile</Label>
                    <Input
                      value={form.emergency_mobile}
                      onChange={(e) =>
                        setForm({ ...form, emergency_mobile: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>E-mail ID</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>PAN No.</Label>
                    <Input
                      value={form.pan_no}
                      onChange={(e) =>
                        setForm({ ...form, pan_no: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Aadhar No.</Label>
                    <Input
                      value={form.aadhar_no}
                      onChange={(e) =>
                        setForm({ ...form, aadhar_no: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Edu Qualification</Label>
                    <Input
                      value={form.edu_qualification}
                      onChange={(e) =>
                        setForm({ ...form, edu_qualification: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Father's Name</Label>
                    <Input
                      value={form.father_name}
                      onChange={(e) =>
                        setForm({ ...form, father_name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>CL Opening Balance</Label>
                    <Input
                      type="number"
                      step="0.5"
                      data-testid="cl-balance-field"
                      value={form.cl_balance}
                      onChange={(e) =>
                        setForm({ ...form, cl_balance: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Label>Address</Label>
                    <Input
                      value={form.address}
                      onChange={(e) =>
                        setForm({ ...form, address: e.target.value })
                      }
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Label>Assigned Project</Label>
                    <Select
                      value={form.project_id || "none"}
                      onValueChange={(v) =>
                        setForm({ ...form, project_id: v === "none" ? "" : v })
                      }
                    >
                      <SelectTrigger data-testid="worker-project-select">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} ({p.client_name})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Offer Letter (PDF/Image, ≤ 5MB)</Label>
                  <div className="border-2 border-dashed border-[#2A8A8F] bg-[#F4F7F9] p-3 rounded-lg flex items-center justify-between">
                    <div className="text-sm text-[#475569]">
                      {form.offer_letter_filename ? (
                        <span className="text-[#1A3A5C] font-semibold inline-flex items-center gap-1">
                          <FileText className="w-4 h-4" />{" "}
                          {form.offer_letter_filename}
                        </span>
                      ) : (
                        "Upload offer letter (optional)"
                      )}
                    </div>
                    <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-[#2A8A8F] font-semibold hover:underline">
                      <Upload className="w-4 h-4" /> Choose file
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={onFile}
                        className="hidden"
                        data-testid="offer-letter-upload"
                      />
                    </label>
                  </div>
                </div>

                {/* Resignation */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-red-800">
                        Mark as Resigned
                      </div>
                      <div className="text-xs text-red-700">
                        Login is disabled. Record moves to "Resigned" tab.
                      </div>
                    </div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        data-testid="resigned-toggle"
                        checked={form.resigned}
                        onChange={(e) =>
                          setForm({ ...form, resigned: e.target.checked })
                        }
                      />
                      <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-red-600 relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                    </label>
                  </div>
                  {form.resigned && (
                    <div>
                      <Label className="text-red-800">
                        Relieving Letter (PDF/Image, ≤ 5MB)
                      </Label>
                      <div className="border-2 border-dashed border-red-400 bg-white p-3 rounded-lg flex items-center justify-between">
                        <div className="text-sm text-[#475569]">
                          {form.relieving_letter_filename ? (
                            <span className="text-red-800 font-semibold inline-flex items-center gap-1">
                              <FileText className="w-4 h-4" />{" "}
                              {form.relieving_letter_filename}
                            </span>
                          ) : (
                            "Upload relieving letter (optional)"
                          )}
                        </div>
                        <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-red-700 font-semibold hover:underline">
                          <Upload className="w-4 h-4" /> Choose file
                          <input
                            type="file"
                            accept=".pdf,image/*"
                            onChange={onRelievingFile}
                            className="hidden"
                            data-testid="relieving-letter-upload"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="save-worker"
                  className="w-full bg-[#1A3A5C] hover:bg-[#0F2236]"
                >
                  {loading ? "Saving..." : "Save Employee"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 pt-3 pb-2 border-b bg-slate-50/50 flex items-center justify-between gap-3">
          <Input
            data-testid="workers-search"
            placeholder="Search by name, ID, mobile, email, dept, project, designation…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
          />
          <div className="text-xs text-[#475569]">
            {(() => {
              const ql = q.trim().toLowerCase();
              const c = ql
                ? rows.filter((r) => {
                    const hay = [
                      r.worker_id, r.name, r.phone, r.email, r.dept,
                      r.designation, r.project_name, r.client_name,
                    ].join(" ").toLowerCase();
                    return hay.includes(ql);
                  }).length
                : rows.length;
              return `${c} / ${rows.length}`;
            })()}
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-[#475569]">
              <th className="px-4 py-3">Emp ID</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Dept / Designation</th>
              <th className="px-4 py-3">Mobile</th>
              <th className="px-4 py-3">Project / Client</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-12 text-center text-[#475569]">
                  No employees yet.
                </td>
              </tr>
            )}
            {(() => {
              const ql = q.trim().toLowerCase();
              const filtered = ql
                ? rows.filter((r) => {
                    const hay = [
                      r.worker_id, r.name, r.phone, r.email, r.dept,
                      r.designation, r.project_name, r.client_name,
                    ].join(" ").toLowerCase();
                    return hay.includes(ql);
                  })
                : rows;
              if (rows.length > 0 && filtered.length === 0) {
                return (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-[#475569]">
                      No results for "{q}".
                    </td>
                  </tr>
                );
              }
              return filtered.map((r) => (
              <tr key={r.id} data-testid={`worker-row-${r.worker_id}`}>
                <td className="px-4 py-3 font-mono font-bold text-[#1A3A5C]">
                  {r.worker_id}
                </td>
                <td className="px-4 py-3">
                  <div>{r.name}</div>
                  {r.email && (
                    <div className="text-xs text-[#475569]">{r.email}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  <div>{r.dept || "—"}</div>
                  <div className="text-xs text-[#475569]">
                    {r.designation || ""}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">{r.phone || "—"}</td>
                <td className="px-4 py-3 text-sm">
                  {r.project_name ? (
                    <>
                      <div className="font-semibold text-[#1A3A5C]">
                        {r.project_name}
                      </div>
                      <div className="text-xs text-[#475569]">
                        {r.client_name}
                      </div>
                    </>
                  ) : (
                    <span className="text-amber-600">Unassigned</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setViewing(r)}
                    data-testid={`view-${r.worker_id}`}
                    className="text-[#2A8A8F] hover:bg-slate-100 p-2 rounded"
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {r.offer_letter_filename && (
                    <button
                      onClick={() => downloadLetter(r.id)}
                      className="text-[#2A8A8F] hover:bg-slate-100 p-2 rounded ml-1"
                      title="Download offer letter"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  {r.resigned && r.relieving_letter_filename && (
                    <button
                      onClick={() => downloadRelievingLetter(r.id)}
                      className="text-red-600 hover:bg-red-50 p-2 rounded ml-1"
                      title="Download relieving letter"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => edit(r)}
                    className="text-[#1A3A5C] hover:bg-slate-100 p-2 rounded ml-1"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    className="text-red-600 hover:bg-red-50 p-2 rounded ml-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ));
            })()}
          </tbody>
        </table>
      </Card>

      {/* Bulk upload */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">
              Bulk Upload Employees
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-[#475569]">
              Upload an Excel (.xlsx) with these columns:{" "}
              <strong>Emp ID, Name of Employee, Dept, Date of Joining, Date of Birth, Gender, Mobile No., Emergency Mobile, E-mail ID, PAN No., Aadhar No., Edu Qualification, Father's Name, Address, Blood Group, Designation, Password</strong>
              .
            </p>
            <Button
              variant="outline"
              onClick={downloadTemplate}
              data-testid="download-template-btn"
              className="w-full"
            >
              <Download className="w-4 h-4 mr-1" /> Download Template
            </Button>

            <div>
              <Label>Assign to Project (optional)</Label>
              <Select value={bulkProject} onValueChange={setBulkProject}>
                <SelectTrigger data-testid="bulk-project-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.client_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Default Password (for rows without one)</Label>
              <Input
                value={bulkPwd}
                onChange={(e) => setBulkPwd(e.target.value)}
              />
            </div>

            <div>
              <Label>Excel file</Label>
              <input
                ref={bulkFileRef}
                type="file"
                accept=".xlsx,.xls"
                data-testid="bulk-file-input"
                onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                className="block w-full text-sm border rounded-lg p-2"
              />
              {bulkFile && (
                <div className="text-xs text-[#475569] mt-1">
                  Selected: {bulkFile.name}
                </div>
              )}
            </div>

            {bulkResult && (
              <div className="bg-slate-50 rounded p-3 text-xs space-y-1">
                <div className="text-[#15803D] font-bold">
                  ✓ {bulkResult.created} created
                </div>
                {bulkResult.skipped_existing?.length > 0 && (
                  <div className="text-amber-700">
                    Skipped (already exist): {bulkResult.skipped_existing.join(", ")}
                  </div>
                )}
                {bulkResult.errors?.length > 0 && (
                  <div className="text-red-600">
                    Errors: {bulkResult.errors.length}
                  </div>
                )}
              </div>
            )}

            <Button
              data-testid="bulk-submit-btn"
              onClick={bulkSubmit}
              disabled={bulkBusy || !bulkFile}
              className="w-full bg-[#1A3A5C] hover:bg-[#0F2236]"
            >
              {bulkBusy ? "Uploading..." : "Upload & Import"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View employee details */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Employee Details</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto pr-2">
              <div className="flex items-center gap-4">
                <div className="bg-[#1A3A5C] text-white rounded-full w-14 h-14 flex items-center justify-center font-display font-bold text-xl">
                  {viewing.name?.slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div className="font-display font-bold text-xl text-[#1A3A5C]">
                    {viewing.name}
                  </div>
                  <div className="font-mono text-[#475569]">
                    {viewing.worker_id}
                  </div>
                  {viewing.project_name && (
                    <div className="text-xs text-[#2A8A8F] font-semibold mt-1">
                      {viewing.project_name} · {viewing.client_name}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 bg-slate-50 rounded-lg p-4">
                {[
                  ["Department", viewing.dept],
                  ["Designation", viewing.designation],
                  ["Date of Joining", viewing.joining_date],
                  ["Date of Birth", viewing.dob],
                  ["Gender", viewing.gender],
                  ["Blood Group", viewing.blood_group],
                  ["Mobile No.", viewing.phone],
                  ["Emergency Mobile", viewing.emergency_mobile],
                  ["E-mail ID", viewing.email],
                  ["PAN No.", viewing.pan_no],
                  ["Aadhar No.", viewing.aadhar_no],
                  ["Edu Qualification", viewing.edu_qualification],
                  ["Father's Name", viewing.father_name],
                  ["CL Opening Balance", viewing.cl_balance],
                ].map(([label, val]) => (
                  <div
                    key={label}
                    className="flex justify-between gap-3 border-b border-slate-200 py-1"
                  >
                    <span className="text-[#475569]">{label}</span>
                    <span className="text-[#1A3A5C] font-medium text-right">
                      {val || "—"}
                    </span>
                  </div>
                ))}
                {viewing.resigned && (
                  <div className="sm:col-span-2 py-2 mt-1 bg-red-50 border border-red-200 rounded px-3 text-red-800 text-sm">
                    <strong>Resigned</strong>{" "}
                    {viewing.resigned_at && (
                      <span>
                        on{" "}
                        {new Date(viewing.resigned_at).toLocaleDateString(
                          "en-IN",
                          {
                            timeZone: "Asia/Kolkata",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </span>
                    )}
                  </div>
                )}
                <div className="sm:col-span-2 py-1">
                  <div className="text-[#475569] mb-1">Address</div>
                  <div className="text-[#1A3A5C] font-medium">
                    {viewing.address || "—"}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end flex-wrap">
                {viewing.offer_letter_filename && (
                  <Button
                    variant="outline"
                    data-testid="view-download-offer"
                    onClick={() => downloadLetter(viewing.id)}
                  >
                    <Download className="w-4 h-4 mr-1" /> Appointment Order
                  </Button>
                )}
                {viewing.relieving_letter_filename && (
                  <Button
                    variant="outline"
                    data-testid="view-download-relieving"
                    onClick={() => downloadRelievingLetter(viewing.id)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Download className="w-4 h-4 mr-1" /> Relieving Letter
                  </Button>
                )}
                <Button
                  onClick={() => {
                    const v = viewing;
                    setViewing(null);
                    edit(v);
                  }}
                  className="bg-[#1A3A5C] hover:bg-[#0F2236]"
                >
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
